import { EthPrice } from "../../../common/types";
import { ResponsiveContainer, XAxis, YAxis, Line, LineChart, Tooltip } from "recharts";
import { formatDayTime, calculateTicks, formatCurrency } from "../../helpers/helpers";
import { useState } from "react";
import { ChartTooltip, secondsDict } from "../utils/ChartUtils";

export function Button({
  text,
  selected,
  setSelected,
  isDarkMode,
}: {
  text: string;
  selected: boolean;
  setSelected: (text: string) => void;
  isDarkMode: boolean;
}) {
  return (
    <button
      className={`rounded-lg border-[1px] px-2 py-1
      ${isDarkMode ? "border-darkgreen" : "border-brown"}
      ${
        selected
          ? isDarkMode
            ? "bg-darkblack text-white"
            : "bg-brown text-white"
          : isDarkMode
            ? "bg-darkblue text-darkgreen"
            : "bg-white text-brown"
      }`}
      onClick={() => setSelected(text)}
    >
      {text}
    </button>
  );
}

export function EthChart({
  ethPrices,
  isDarkMode,
}: {
  ethPrices: EthPrice[];
  isDarkMode: boolean;
}) {
  const [selected, setSelected] = useState("1D");

  const data = ethPrices.filter(
    (price) => price.timestamp > Date.now() / 1000 - secondsDict[selected],
  );

  return (
    <div
      className={`my-2 ml-4 mr-4 flex w-[80%] flex-col items-start justify-center rounded-lg border-2 p-4 text-sm ${isDarkMode ? "border-darkgreen bg-darkblue text-white [box-shadow:inset_0-2px_#37b764]" : "border-brown bg-white [box-shadow:inset_0_-2px_#342e2e]"}`}
    >
      <div className="flex w-full justify-between">
        <div className="mb-4 font-sans text-xl">$ETH</div>
        <div className="mb-4 space-x-1 font-sans text-sm">
          <Button
            isDarkMode={isDarkMode}
            text="1D"
            selected={selected === "1D"}
            setSelected={setSelected}
          />
          <Button
            isDarkMode={isDarkMode}
            text="7D"
            selected={selected === "7D"}
            setSelected={setSelected}
          />
          <Button
            isDarkMode={isDarkMode}
            text="1M"
            selected={selected === "1M"}
            setSelected={setSelected}
          />
        </div>
      </div>
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={data} margin={{ left: 15 }}>
          <XAxis
            dataKey="timestamp"
            type="number"
            tickFormatter={formatDayTime}
            ticks={calculateTicks(data)}
            domain={["dataMin", "dataMax"]}
            stroke={isDarkMode ? "#37b764" : "#342e2e"}
          />
          <YAxis
            dataKey="eth_price"
            stroke={isDarkMode ? "#37b764" : "#342e2e"}
            domain={["auto", "auto"]}
            tickFormatter={formatCurrency}
          />
          <Line
            type="monotone"
            dataKey="eth_price"
            stroke={isDarkMode ? "#37b764" : "#342e2e"}
            dot={false}
          />
          <Tooltip
            content={<ChartTooltip isDarkMode={isDarkMode} unit="$" valueDecimals={2} />}
            cursor={{ stroke: isDarkMode ? "#37b764" : "#342e2e", strokeWidth: 1 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
